import { createSlice } from '@reduxjs/toolkit';
import { createKey } from 'src/utils/CaseDetail.utils';
import { CONTACT_TYPES, STORE_KEYS } from '../constants';
import {
  CaseDetailsPageData,
  CustomerDetailApiResponse,
  ITelePhoneData,
  PendingEmiSchedule,
  ReduxCaseDetail
} from '../interfaces/CaseDetail.type';
import { PhoneNumberSourceText } from '@cp/src/components/Ameyo/interfaces';
import { getSanitizedEnumValue } from '@cp/src/utils/commonUtils';
import { feeWaiveTransformedEmiData } from '../components/EmiSchedule/utils';
import { AlertType } from '../components/CustomerSummary/constant';

const initialState: ReduxCaseDetail = {
  detailsApiLoading: false,
  pageData: {},
  caseDetailTabsFeatureFlag: {},
  customerConfidence: {
    telephoneCustomerConfidence: [],
    longhornSecondarySources: []
  },
  callAttemptLimitAlert: {
    alertType: AlertType.NONE,
    alertMessage: null,
    alertDetails: null
  }
};

const caseDetailsSlice = createSlice({
  name: 'caseDetail',
  initialState,
  reducers: {
    setTelephones: (state, action) => {
      const { lan, customerRefrenceId, data } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const __internal__lastUpdatedAt = new Date().getTime();
      const sortedTelephones = data.sort(
        (telephones1: ITelePhoneData, telephones2: ITelePhoneData) => {
          return telephones2?.telephoneScore - telephones1?.telephoneScore;
        }
      );
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.TELEPHONES]: {
            loading: false,
            error: false,
            data: sortedTelephones
          },
          __internal__lastUpdatedAt
        }
      };
    },
    setContacts: (state, action) => {
      const { lan, customerRefrenceId, data, contactType } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const __internal__lastUpdatedAt = new Date().getTime();
      const storeKeyName =
        contactType === CONTACT_TYPES.SKIP_TRACE
          ? STORE_KEYS.SKIP_TRACING_DATA
          : STORE_KEYS.SPADE_DATA;
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [storeKeyName]: {
            loading: false,
            error: false,
            data: data
          },
          __internal__lastUpdatedAt
        }
      };
    },
    setCustomerConfidence: (state, action) => {
      state.customerConfidence = action.payload;
    },
    setCustomerInfo: (state, action) => {
      const { lan, customerRefrenceId, data } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const __internal__lastUpdatedAt = new Date().getTime();

      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.CUSTOMER_INFO]: {
            loading: false,
            error: false,
            data: data
          },
          __internal__lastUpdatedAt
        }
      };
    },
    setMonthlyEnach: (state, action) => {
      const { lan, customerRefrenceId, data } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const __internal__lastUpdatedAt = new Date().getTime();

      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.MONTHLY_ENACH]: {
            loading: false,
            error: false,
            data: data
          },
          __internal__lastUpdatedAt
        }
      };
    },
    // for update and create
    setCaseDetail: (state, action) => {
      const { lan, customerRefrenceId, key, data } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || ({} as CaseDetailsPageData);
      const __internal__lastUpdatedAt = new Date().getTime();
      // monitoring api calls based on keys
      if (key === STORE_KEYS.DETAILS) state.detailsApiLoading = false;

      const {
        dues = [],
        pendingEmiSchedules = [],
        partPrePayments = [],
        unpaidDays
      } = (data as CustomerDetailApiResponse) || {};
      const duesWithNewWaiverInfo = dues.map(due => feeWaiveTransformedEmiData(due, unpaidDays));
      const allDues = [...duesWithNewWaiverInfo, ...pendingEmiSchedules, ...partPrePayments].sort(
        (a, b) => {
          const date1 = new Date(a.dueDate).getTime();
          const date2 = new Date(b.dueDate).getTime();
          return date1 - date2;
        }
      );
      data.totalDuesCount = duesWithNewWaiverInfo.length + pendingEmiSchedules.length;
      data.allDues = allDues;
      // trying to get second last emi record as last emi record doesnot match with the actual emi amount
      const secondLastEmiRecord = allDues?.[allDues?.length - 2] as PendingEmiSchedule;
      const lastEmiRecord = allDues?.at?.(-1) as PendingEmiSchedule;
      if (lastEmiRecord) {
        newData.emiSummary = {
          lastEmiDate: lastEmiRecord?.endDate
        };
      }
      if (secondLastEmiRecord) {
        newData.emiSummary = {
          ...newData.emiSummary,
          emiAmount: (secondLastEmiRecord?.principal || 0) + (secondLastEmiRecord?.interest || 0)
        };
      }
      delete data.pendingEmiSchedules;
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [key]: {
            loading: false,
            error: false,
            data: data
          },
          __internal__lastUpdatedAt
        }
      };
    },
    setCaseDetailLoading: (state, action) => {
      const { lan, customerRefrenceId, key, loading } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] as CaseDetailsPageData;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const oldKeyData = (newData && newData[key]) || {};
      const __internal__lastUpdatedAt = new Date().getTime();
      // monitoring api calls based on keys
      if (key === STORE_KEYS.DETAILS) state.detailsApiLoading = false;

      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [key]: {
            ...oldKeyData,
            loading: loading
          },
          __internal__lastUpdatedAt
        }
      };
    },
    setCaseDetailError: (state, action) => {
      const { lan, customerRefrenceId, key, error } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] as CaseDetailsPageData;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const oldKeyData = (newData && newData[key]) || {};
      const __internal__lastUpdatedAt = new Date().getTime();
      // monitoring api calls based on keys
      if (key === STORE_KEYS.DETAILS) state.detailsApiLoading = false;

      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [key]: {
            ...oldKeyData,
            loading: false,
            error: error
          },
          __internal__lastUpdatedAt
        }
      };
    },
    // for reset
    resetCaseDetail: (state, action) => {
      const { lan, customerRefrenceId } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      state.pageData = {
        ...state.pageData,
        [pageKey]: null
      };
    },
    setCaseDetailTabDataLoading(state, action) {
      const { lan, customerRefrenceId, key, data } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const caseDetailsData = state.pageData[pageKey];
      const tabData = state?.pageData[pageKey];
      if (!caseDetailsData) {
        return;
      }
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...caseDetailsData,
          [key]: {
            ...tabData,
            loading: data
          }
        }
      };
    },
    setCaseDetailTabData(state, action) {
      const { lan, customerRefrenceId, key, data } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const caseDetailsData = state.pageData[pageKey];
      if (!caseDetailsData) {
        return;
      }
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...caseDetailsData,
          [key]: {
            data: data,
            loading: false
          }
        }
      };
    },

    setEmiBreakupLoading: (state, action) => {
      const { lan, customerRefrenceId, key, data, loading } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const emiBreakUpDate = state?.pageData[pageKey]?.emiBreakup;
      const __internal__lastUpdatedAt = new Date().getTime();

      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.EMI_BREAKUP]: {
            ...emiBreakUpDate,
            [key]: {
              data,
              loading: loading
            }
          },
          __internal__lastUpdatedAt
        }
      };
    },

    setEmiBreakup: (state, action) => {
      const { lan, customerRefrenceId, key, data } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const emiBreakUpDate = state?.pageData[pageKey]?.emiBreakup || {};
      const __internal__lastUpdatedAt = new Date().getTime();

      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.EMI_BREAKUP]: {
            ...emiBreakUpDate,
            [key]: {
              loading: false,
              error: false,
              data: data
            }
          },
          __internal__lastUpdatedAt
        }
      };
    },

    setEmiBreakupError: (state, action) => {
      const { lan, customerRefrenceId, key } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const emiBreakUpDate = state?.pageData[pageKey]?.emiBreakup;
      const __internal__lastUpdatedAt = new Date().getTime();

      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.EMI_BREAKUP]: {
            ...emiBreakUpDate,
            [key]: {
              loading: false,
              error: true
            }
          },
          __internal__lastUpdatedAt
        }
      };
    },

    setRepaymentDue: (state, action) => {
      const { lan, customerRefrenceId, key, data } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const repaymentDueUpdate = state?.pageData[pageKey]?.repaymentDue || {};
      const __internal__lastUpdatedAt = new Date().getTime();
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.REPAYMENT_DUE]: {
            ...repaymentDueUpdate,
            [key]: {
              data,
              loading: false,
              error: false
            }
          },
          __internal__lastUpdatedAt
        }
      };
    },

    setRepaymentDueLoading: (state, action) => {
      const { lan, customerRefrenceId, key, data, loading } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const repaymentDueUpdate = state?.pageData[pageKey]?.repaymentDue || {};
      const __internal__lastUpdatedAt = new Date().getTime();
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.REPAYMENT_DUE]: {
            ...repaymentDueUpdate,
            [key]: {
              data,
              loading: loading,
              error: false
            }
          },
          __internal__lastUpdatedAt
        }
      };
    },

    setRepaymentDueError: (state, action) => {
      const { lan, customerRefrenceId, key } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const repaymentDueUpdate = state?.pageData[pageKey]?.repaymentDue || {};
      const __internal__lastUpdatedAt = new Date().getTime();
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.REPAYMENT_DUE]: {
            ...repaymentDueUpdate,
            [key]: {
              loading: false,
              error: true
            }
          },
          __internal__lastUpdatedAt
        }
      };
    },
    setRepaymentHistory: (state, action) => {
      const { lan, customerRefrenceId, data } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const __internal__lastUpdatedAt = new Date().getTime();
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.REPAYMENTS_HISTORY]: {
            loading: false,
            error: false,
            data: data
          },
          __internal__lastUpdatedAt
        }
      };
    },
    setInteractionRowDetailedView: (state, action) => {
      const { lan, customerRefrenceId, data, pages } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const __internal__lastUpdatedAt = new Date().getTime();
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.CALL_INTERACTIONS]: {
            loading: false,
            error: false,
            data: {
              data,
              pages: pages
            }
          },
          __internal__lastUpdatedAt
        }
      };
    },
    setTelephonesV2: (state, action) => {
      const { lan, customerRefrenceId, data } = action.payload;
      const pageKey = createKey(lan, customerRefrenceId);
      const newData = state?.pageData[pageKey] || {};
      const __internal__lastUpdatedAt = new Date().getTime();
      const newTelephoneData = data.map((item: ITelePhoneData) => {
        return {
          ...item,
          source: item.oldestSource,
          sources: item?.sourceText.map(i => {
            return getSanitizedEnumValue(PhoneNumberSourceText, i);
          })
        };
      });
      state.pageData = {
        ...state.pageData,
        [pageKey]: {
          ...newData,
          [STORE_KEYS.TELEPHONES_V2]: {
            loading: false,
            error: false,
            data: newTelephoneData
          },
          __internal__lastUpdatedAt
        }
      };
    },
    setCaseDetailTabsFeatureFlag: (state, action) => {
      const { key, value } = action.payload;
      state.caseDetailTabsFeatureFlag[key] = value;
    },
    resetCaseDetailTabsFeatureFlag: state => {
      state.caseDetailTabsFeatureFlag = {};
    },
    setCallAttemptLimitAlert: (state, action) => {
      state.callAttemptLimitAlert = action.payload;
    }
  }
});

export const {
  setCaseDetail,
  resetCaseDetail,
  setCaseDetailLoading,
  setCaseDetailError,
  setEmiBreakupLoading,
  setEmiBreakup,
  setEmiBreakupError,
  setRepaymentDue,
  setRepaymentDueError,
  setRepaymentDueLoading,
  setTelephones,
  setMonthlyEnach,
  setContacts,
  setCustomerConfidence,
  setCustomerInfo,
  setRepaymentHistory,
  setTelephonesV2,
  setInteractionRowDetailedView,
  setCaseDetailTabsFeatureFlag,
  resetCaseDetailTabsFeatureFlag,
  setCallAttemptLimitAlert
} = caseDetailsSlice.actions;

export default caseDetailsSlice.reducer;
